/**
 * Pure helpers for the SCO offboarding form: the empty form value, the deletion-date
 * default, validation, the user option label and the submit payload.
 *
 * They are kept free of React so the page and the form component can share them and so
 * the rules can be tested without rendering anything.
 */

/** Days between the end date and the deletion date when the settings do not say. */
export const DEFAULT_DELETION_DAYS = 90;

/** The blank offboarding form value. A fresh form always starts from a copy of this. */
export const EMPTY_OFFBOARDING = {
  userPublicId: "",
  endDate: "",
  deletionDate: "",
  computerDisposition: "",
  convertToSharedMailbox: false,
  sharedMailboxAccessUserPublicIds: [],
  forwardMail: false,
  forwardToUserPublicId: "",
  autoReplyText: "",
  comment: "",
};

/**
 * Shifts an ISO date (yyyy-MM-dd) by a number of days in UTC, so the browser's timezone
 * cannot move the result to the neighbouring day. Mirrors `shiftIsoDate` in
 * CreateUserPage.
 *
 * @param {string} isoDate - Date in yyyy-MM-dd form
 * @param {number} days - Days to add; negative shifts backwards
 * @returns {string} The shifted date in yyyy-MM-dd form, or "" if the input is unusable
 */
const shiftIsoDate = (isoDate, days) => {
  if (!isoDate) return "";

  const parsed = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "";

  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
};

/**
 * The deletion date suggested for an end date: the customer's configured number of days
 * after the account is closed down. A customer without a configured number falls back to
 * DEFAULT_DELETION_DAYS rather than to "the same day", which would delete immediately.
 *
 * @param {string} endDate - The end date in yyyy-MM-dd form
 * @param {number} [days] - Days between the end date and the deletion date
 * @returns {string} The deletion date in yyyy-MM-dd form, or "" without an end date
 */
export const defaultDeletionDate = (endDate, days = DEFAULT_DELETION_DAYS) =>
  shiftIsoDate(endDate, Number.isFinite(days) ? days : DEFAULT_DELETION_DAYS);

/**
 * Validates an offboarding form value.
 *
 * The end date may be in the past (an offboarding is often registered after the fact),
 * but the deletion date must be both after the end date and still ahead of us — a date
 * that has already passed cannot be scheduled.
 *
 * @param {object} value - The current form value
 * @param {string} todayIso - Today's date in yyyy-MM-dd form
 * @returns {string|null} The first problem found, or null when the value is valid
 */
export const validateOffboarding = (value, todayIso) => {
  if (!value?.userPublicId) return "Choose the user to offboard";
  if (!value.endDate) return "Choose the end date";
  if (!value.deletionDate) return "Choose the deletion date";

  if (value.deletionDate <= value.endDate) {
    return "The deletion date must be after the end date";
  }

  if (value.deletionDate <= todayIso) {
    return "The deletion date must be in the future";
  }

  if (
    value.convertToSharedMailbox &&
    (value.sharedMailboxAccessUserPublicIds || []).length === 0
  ) {
    return "Choose who should have access to the shared mailbox";
  }

  if (value.forwardMail && !value.forwardToUserPublicId) {
    return "Choose who should receive the forwarded mail";
  }

  return null;
};

/**
 * Labels a user in the pickers. The manager is part of the label because several
 * customers have people with the same name, and the manager is what tells them apart.
 *
 * @param {object} user - A user from the tenant
 * @returns {string} "Name · principal name · Manager: name"
 */
export const userOptionLabel = (user) => {
  const principalName = user?.userPrincipalName || user?.email || "";
  const name = user?.displayName || principalName;
  const manager = user?.managerDisplayName || "-";

  return `${name} · ${principalName} · Manager: ${manager}`;
};

/** Turns a blank string into null, so the API stores "not answered" rather than "". */
const blankToNull = (text) => {
  const trimmed = (text || "").trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Builds the payload posted to the offboarding endpoint.
 *
 * The wishes belonging to a switched-off toggle are dropped rather than sent: a
 * forward-to user left behind from an earlier choice must not reach the mailbox rules.
 *
 * @param {object} value - The current form value
 * @param {{source: string, tenantPublicId: string}} context - Where the request comes from
 * @returns {object} The request body for `submitOffboarding`
 */
export const buildOffboardingPayload = (value, { source, tenantPublicId }) => ({
  source,
  tenantPublicId,
  userPublicId: value.userPublicId,
  endDate: value.endDate,
  deletionDate: value.deletionDate,
  computerDisposition: blankToNull(value.computerDisposition),
  convertToSharedMailbox: !!value.convertToSharedMailbox,
  sharedMailboxAccessUserPublicIds: value.convertToSharedMailbox
    ? [...(value.sharedMailboxAccessUserPublicIds || [])]
    : [],
  forwardMail: !!value.forwardMail,
  forwardToUserPublicId: value.forwardMail
    ? value.forwardToUserPublicId || null
    : null,
  autoReplyText: blankToNull(value.autoReplyText),
  comment: blankToNull(value.comment),
});
